/**
 * Shared constants, types, and helpers for the Context Dashboard tabs.
 * Extracted from the monolithic ContextDashboard.tsx.
 */

import { supabase } from "@/integrations/supabase/client";

// ─── Constants ──────────────────────────────────────────────────────
export const WORKER_URL = import.meta.env.VITE_WORKER_URL || "https://n71-mission-worker.fly.dev";
// Derive from the configured worker so the gov build shows its own MCP endpoint,
// never the hardcoded consumer host.
export const MCP_ENDPOINT = `${WORKER_URL}/brain/mcp`;

/**
 * Sovereign / ministry edition flag — build-time. The entire deployed build
 * IS the ministry edition (a regulated government deployment), so this is a
 * static `VITE_SOVEREIGN_EDITION=true` env baked in at build time rather than
 * a per-workspace runtime toggle.
 *
 * When true, the dashboard hides every tab whose backend (autopilot:
 * Thoughts / Chat / Work / Connect) is disabled in the sovereign deployment,
 * leaving only Memory + Team. A minister must never click a tab that 404s or
 * is permanently empty. When unset/false this is a no-op and the normal N71
 * product is completely unchanged.
 */
export const SOVEREIGN_EDITION = import.meta.env.VITE_SOVEREIGN_EDITION === "true";

/**
 * Sovereign deployment flag — build-time (VITE_SOVEREIGN=true on the gov build).
 * UNLIKE SOVEREIGN_EDITION this does NOT collapse the nav. It gates UX HONESTY:
 * hide consumer affordances that physically cannot work on a sovereign tenant
 * (pull connectors / OAuth — egress-blocked; public signup + waitlist; billing /
 * credits / pricing) and surface the real data-in model (document upload + the
 * push API). Decoupled so the gov tenant keeps full nav while the UI stops
 * showing buttons that fail or mislead.
 */
export const SOVEREIGN = import.meta.env.VITE_SOVEREIGN === "true";

// ─── Auth + fetch helper ────────────────────────────────────────────
export async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function workerFetch<T>(path: string, workspaceId: string, options?: RequestInit): Promise<T | null> {
  const token = await getAuthToken();
  if (!token) return null;
  const sep = path.includes("?") ? "&" : "?";
  const res = await fetch(`${WORKER_URL}${path}${sep}workspace_id=${workspaceId}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options?.headers || {}),
    },
  });
  if (!res.ok) {
    console.error(`[ContextDashboard] ${path} returned ${res.status}`);
    return null;
  }
  return res.json();
}

// ─── Animation presets ──────────────────────────────────────────────
export const EASE = [0.16, 1, 0.3, 1] as const;
export const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: EASE },
} as any;
export const stagger = {
  animate: { transition: { staggerChildren: 0.06 } },
};

// ─── Design tokens ──────────────────────────────────────────────────
export const CARD = "rounded-2xl border border-foreground/[0.08] bg-foreground/[0.02] p-6";
export const CARD_SM = "rounded-xl border border-foreground/[0.08] bg-foreground/[0.02] p-4";
export const MICRO = "text-[10px] uppercase tracking-widest text-foreground/40 font-medium";
export const SECTION_HEADING = "text-lg font-serif text-foreground/80 mb-4";

// ─── Types ──────────────────────────────────────────────────────────
export interface ProactiveInsight {
  id: string;
  title: string;
  rationale: string;
  type: string;
  urgency: "low" | "medium" | "high";
  created_at: string;
}

export interface ConnectorHealth {
  provider: string;
  status: "healthy" | "stale" | "degraded" | "broken";
  last_sync_at: string | null;
  minutes_since_sync: number;
  events_last_24h: number;
  events_total: number;
  oauth_status: string;
  errors_last_24h: number;
  last_error: string | null;
}

export interface AuditEntry {
  id: string;
  agent: string;
  tool_name: string;
  created_at: string;
  summary?: string;
}

export interface ConnectedAgent {
  agent: string;
  last_activity: string;
  call_count: number;
}

export interface ApiKeyRecord {
  id: string;
  label: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
}

export interface KGEntity {
  id: string;
  name: string;
  type: string;
  source?: string;
  created_at: string;
}

export interface SharedPageRecord {
  id: string;
  short_id: string;
  title: string;
  template: string;
  public_url: string;
  is_public: boolean;
  view_count: number;
  created_at: string;
  expires_at: string | null;
  storage_path: string;
}

export interface OAuthCredentials {
  id: string;
  client_id: string;
  client_secret_prefix: string;
  name: string;
  capabilities: string[];
  created_at: string;
}

// ─── Helpers ────────────────────────────────────────────────────────
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export const urgencyBadge: Record<string, string> = {
  high: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  low: "bg-foreground/[0.04] text-foreground/40 border-foreground/[0.06]",
};

export const AGENT_ICONS: Record<string, string> = {
  "claude-desktop": "Claude Desktop",
  "cursor": "Cursor",
  "chatgpt": "ChatGPT",
  "copilot": "GitHub Copilot",
  "windsurf": "Windsurf",
  "api": "API",
};

export const CONNECTABLE_TOOLS = [
  { id: "gsuite", name: "Google Workspace", description: "Gmail, Calendar & Drive" },
  { id: "slack", name: "Slack", description: "Team communication" },
  { id: "whatsapp_personal", name: "WhatsApp", description: "Personal messaging" },
  { id: "notion", name: "Notion", description: "Docs & wikis" },
  { id: "github", name: "GitHub", description: "Code & issues" },
  { id: "linear", name: "Linear", description: "Issue tracking" },
  { id: "teams", name: "Microsoft Teams", description: "Team communication" },
  { id: "outlook", name: "Outlook", description: "Email & Calendar" },
  { id: "shopify", name: "Shopify", description: "Ecommerce store" },
];
